import { type RouteRecordRaw, createRouter, createWebHistory } from 'vue-router';

import { GlobalState } from './global';
import { AdminRoutes } from './modules/admin/routes';
import LoginSso from './modules/core/login/f-sso.vue';
import Welcome from './modules/core/welcome.vue';
import { CRMRoutes } from './modules/crm/routes';
import { DisplayRoutes } from './modules/display/routes';
import { FormsRoutes } from './modules/forms/routes';
import { HostingRoutes } from './modules/hosting/routes';
import { InventoryRoutes } from './modules/inventory/routes';
import { ItRoutes } from './modules/it/routes';
import { MspRoutes } from './modules/msp/routes';
import { RmmRoutes } from './modules/rmm/routes';
import { SalesRoutes } from './modules/sales/routes';
import { SysadminRoutes } from './modules/sysadmin/routes';
import { TalkRoutes } from './modules/talk/routes';
import { WorkstationRoutes } from './modules/workstation/routes';
import Workstation from './modules/workstation/workstation.vue';
import { wrapRuntimeImport } from './shared/helpers/async.helpers';
import { makePermissionMeta } from './shared/helpers/permissions.helpers';
import { useStore } from './store';

declare module 'vue-router' {
    interface RouteMeta {
        public?: boolean;
        admin?: boolean;
        // Cross-tenant platform operator console. Gated by the account-level
        // isSysAdmin flag (master-tenant admin) in computeAuthorized — distinct
        // from `admin`, which is per-tenant (tenant.isAdmin).
        sysadmin?: boolean;
        // The MSP-partner surface (/msp). Gated on the `msp.login` capability in
        // computeAuthorized. Deliberately NOT `makePermissionMeta('msp')` — that meta
        // is special-cased to the RMM org-access check, which is a different gate.
        // A route here may narrow further with `feature`, read as a second `msp.*`
        // capability on top of login (e.g. `inventoryRead` for the tenant overview).
        msp?: boolean;
        app?: string; // use makePermissionMeta
        feature?: string | (() => string);
        // Sidebar wordmark ("Zyno{rootAppName}") + canvas breadcrumb. Set on the
        // top-level app route (inherited by children via merged meta). A central
        // afterEach guard applies these to the store, so switching apps is atomic
        // — no lifecycle gap that would flash the default. Routes that omit
        // rootAppName fall back to "Suite" (e.g. Admin, the welcome screen).
        rootAppName?: string;
        subAppName?: string;
        // Strip the sidebar down to Help + the user menu (hides app nav and the
        // phone/notifications/check-ins dock). Used for standalone flows like
        // workstation registration where in-app navigation is a distraction.
        minimalSidebar?: boolean;
    }
}

const routes: RouteRecordRaw[] = [
    {
        path: '/',
        component: Welcome
    },
    {
        path: '/zw',
        component: Workstation,
        children: WorkstationRoutes
    },
    {
        path: '/admin',
        component: wrapRuntimeImport(() => import('./modules/admin/admin.vue')),
        meta: { rootAppName: 'Admin' },
        children: AdminRoutes
    },
    {
        path: '/crm',
        component: wrapRuntimeImport(() => import('./modules/crm/crm.vue')),
        meta: { rootAppName: 'CRM' },
        children: CRMRoutes
    },
    {
        path: '/display',
        component: wrapRuntimeImport(() => import('./modules/display/display.vue')),
        meta: { rootAppName: 'Display' },
        children: DisplayRoutes
    },
    {
        path: '/forms',
        component: wrapRuntimeImport(() => import('./modules/forms/forms.vue')),
        meta: { rootAppName: 'Forms' },
        children: FormsRoutes
    },
    {
        path: '/hosting',
        component: wrapRuntimeImport(() => import('./modules/hosting/hosting.vue')),
        meta: { ...makePermissionMeta('hosting', 'sitesRead'), rootAppName: 'Hosting' },
        children: HostingRoutes
    },
    {
        path: '/inventory',
        component: wrapRuntimeImport(() => import('./modules/inventory/inventory.vue')),
        meta: { rootAppName: 'Inventory' },
        children: InventoryRoutes
    },
    {
        path: '/it',
        component: wrapRuntimeImport(() => import('./modules/it/it.vue')),
        meta: { rootAppName: 'IT' },
        children: ItRoutes
    },
    {
        // MSP-partner surface. No rootAppName: it is a Suite-level partner console rather than a
        // product of its own, so the wordmark stays "ZynoSuite" and each screen supplies its own
        // subAppName ("Talk Quotes"). `msp: true` is inherited by the children for the route guard.
        path: '/msp',
        component: wrapRuntimeImport(() => import('./modules/msp/msp.vue')),
        meta: { msp: true },
        children: MspRoutes
    },
    {
        path: '/rmm',
        component: wrapRuntimeImport(() => import('./modules/rmm/rmm.vue')),
        meta: { ...makePermissionMeta('msp'), rootAppName: 'RMM' },
        children: RmmRoutes
    },
    {
        path: '/sales',
        component: wrapRuntimeImport(() => import('./modules/sales/sales.vue')),
        meta: { rootAppName: 'Sales' },
        children: SalesRoutes
    },
    {
        path: '/sysadmin',
        component: wrapRuntimeImport(() => import('./modules/sysadmin/sysadmin.vue')),
        meta: { sysadmin: true, rootAppName: 'SysAdmin' },
        children: SysadminRoutes
    },
    {
        name: 'talk',
        path: '/talk',
        component: wrapRuntimeImport(() => import('./modules/talk/talk.vue')),
        meta: { ...makePermissionMeta('talk'), rootAppName: 'Talk' },
        children: TalkRoutes
    },

    {
        // Loopback sign-in handoff for local CLIs. The CLI binds 127.0.0.1:<port>, then opens us at
        // /cli/login?callback=…&state=…; we POST the session grant back to that listener. NOT public,
        // so the normal login view shows first and this renders — query intact — once signed in. No
        // permission meta: any signed-in user may authorize a CLI as themselves.
        name: 'cli:login',
        path: '/cli/login',
        component: wrapRuntimeImport(() => import('./modules/core/cli-login.vue')),
        meta: { minimalSidebar: true }
    },

    {
        name: 'login:reauthentication-callback',
        path: '/login/reauthentication-callback',
        component: wrapRuntimeImport(() => import('./modules/core/login/f-reauthentication-callback.vue')),
        meta: { public: true }
    },
    {
        name: 'login:sso',
        path: '/login/sso',
        component: LoginSso,
        meta: { public: true }
    },
    {
        name: 'login:callback',
        path: '/login/callback',
        component: LoginSso,
        meta: { public: true }
    }
];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes
});
GlobalState.router = router;

// Apply the sidebar wordmark + breadcrumb names from route meta on every
// navigation. Atomic with the route change (no component-lifecycle gap), so
// switching apps never flashes the default and a route that omits these falls
// back cleanly. Routes with a *dynamic* subapp name (e.g. a CRM contact's name)
// leave meta.subAppName unset and set `dynamicSubAppName` via useSubAppName —
// which is a separate field precisely so this guard can't clear it out from
// under a component that is still mounted (see the store's subAppName getter).
router.afterEach(to => {
    const store = useStore();
    store.rootAppName = to.meta.rootAppName ?? null;
    store.routeSubAppName = to.meta.subAppName ?? null;
});

export default router;
